import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { signIn, signOut, getCurrentUser, fetchAuthSession, confirmSignIn } from 'aws-amplify/auth';

interface AuthContextType {
  user: any | null;
  token: string | null;
  loading: boolean;
  signIn: (username: string, password: string) => Promise<{ requiresMFA: boolean }>;
  confirmMFA: (code: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      
      setUser(currentUser);
      setToken(idToken || null);
    } catch (error) {
      // User not authenticated
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const handleSignIn = async (username: string, password: string) => {
    try {
      const signInOutput = await signIn({ username, password });
      
      // Handle NEW_PASSWORD_REQUIRED challenge if needed
      if (signInOutput.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        throw new Error('Password change required. Please contact administrator.');
      }
      
      // Handle MFA challenge (Email, SMS, or TOTP)
      // Note: Amplify v6 uses different step names than v5
      if (signInOutput.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE' ||
          signInOutput.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' || 
          signInOutput.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        return { requiresMFA: true };
      }
      
      // Sign in successful, no MFA required
      await checkAuth();
      return { requiresMFA: false };
    } catch (error: any) {
      const errorMessage = error.message || error.name || 'Failed to sign in';
      throw new Error(errorMessage);
    }
  };

  const handleConfirmMFA = async (code: string) => {
    try {
      await confirmSignIn({ challengeResponse: code });
      
      // MFA confirmed, check auth status
      await checkAuth();
    } catch (error: any) {
      const errorMessage = error.message || error.name || 'Failed to confirm MFA code';
      throw new Error(errorMessage);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
      setToken(null);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to sign out');
    }
  };

  const handleRefreshToken = async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session.tokens?.idToken?.toString();
      setToken(idToken || null);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to refresh token');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        signIn: handleSignIn,
        confirmMFA: handleConfirmMFA,
        signOut: handleSignOut,
        refreshToken: handleRefreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

